import React, { useState, useEffect, useCallback, useMemo } from 'react';
import SlotCard from './SlotCard';
import FavoritesPreview from './FavoritesPreview';
import BatchTest from './BatchTest';
import PromotionalImages from './PromotionalImages';
import PackageSearch from './PackageSearch';
import CsvExport from './CsvExport';
import BuyAllSlotCard from './BuyAllSlotCard';
import ReferencePoses from './ReferencePoses';
import PopularityReport from './PopularityReport';
import { fetchPackages, fetchLayout, fetchLayoutPoses, saveLayout, fetchBackgroundSets, fetchBackgrounds } from '../shared/api';
import { FullPageSpinner, InlineSpinner } from '../shared/Spinner';
import { FAVORITES_SEASONS as SEASONS, FAVORITES_SEASON_VALUES as SEASON_VALUES } from '../shared/seasons';

// Default 12-slot layout: Row 1: buy_all + 3 vertical, Row 2: 2 horizontal, Row 3: 4 vertical, Row 4: 2 horizontal
const DEFAULT_SLOTS = [
  { slot_id: 'buy_all', slot_type: 'buy_all', row_number: 0, position: 0 },
  { slot_id: 'v2', slot_type: 'portrait',  row_number: 0, position: 1 },
  { slot_id: 'v3', slot_type: 'portrait',  row_number: 0, position: 2 },
  { slot_id: 'v4', slot_type: 'portrait',  row_number: 0, position: 3 },
  { slot_id: 'h1', slot_type: 'landscape', row_number: 1, position: 0 },
  { slot_id: 'h2', slot_type: 'landscape', row_number: 1, position: 1 },
  { slot_id: 'v5', slot_type: 'portrait',  row_number: 2, position: 0 },
  { slot_id: 'v6', slot_type: 'portrait',  row_number: 2, position: 1 },
  { slot_id: 'v7', slot_type: 'portrait',  row_number: 2, position: 2 },
  { slot_id: 'v8', slot_type: 'portrait',  row_number: 2, position: 3 },
  { slot_id: 'h3', slot_type: 'landscape', row_number: 3, position: 0 },
  { slot_id: 'h4', slot_type: 'landscape', row_number: 3, position: 1 },
];

// Season list is the shared single source of truth (../shared/seasons) so the
// Layout tab and the Look editor can't drift; it must stay in sync with
// LookTemplate::SPEC_PREFIX (enforced by a guard spec).

const FavoritesLayoutEditor = ({ looks, loading, onDirtyChange, onReferencePosesChange }) => {
  const [packages, setPackages] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(SEASON_VALUES[0]);
  const [slots, setSlots] = useState(DEFAULT_SLOTS.map(s => ({ ...s, look_template_id: null, background_id: null, background_image_url: null })));
  const [layoutId, setLayoutId] = useState(null);
  const [buyAllS3Path, setBuyAllS3Path] = useState(null);
  const [backgroundSets, setBackgroundSets] = useState([]);
  const [backgroundSetId, setBackgroundSetId] = useState(null);
  // Backgrounds in the currently-selected set, keyed by id. Used to resolve
  // slot.background_image_url when the server response arrives without one
  // (transient Paperclip/S3 URL issues). Truth-of-record is slot.background_id.
  const [backgroundsById, setBackgroundsById] = useState({});
  const [referencePoses, setReferencePoses] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [loadingLayout, setLoadingLayout] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveFlash, setSaveFlash] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showBatchTest, setShowBatchTest] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [dragOverSlotId, setDragOverSlotId] = useState(null);
  const dragSourceRef = React.useRef(null);

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    setLoadingPackages(true);
    Promise.all([
      fetchPackages().then(setPackages).catch(console.error),
      fetchBackgroundSets().then(setBackgroundSets).catch(console.error),
    ]).finally(() => setLoadingPackages(false));
  }, []);

  // Pick a package: also snap the background set + last-edited season so
  // switching pricelists reopens exactly where the layout was last saved
  // (no manual re-selecting of the season each time).
  // When the background set changes, fetch its backgrounds once and cache the
  // id→url map. This is what the picker also uses; caching it here lets slot
  // cards fall back to it when a saved slot's server-side URL is missing.
  useEffect(() => {
    if (!backgroundSetId) { setBackgroundsById({}); return; }
    fetchBackgrounds(backgroundSetId)
      .then((list) => {
        const map = {};
        (list || []).forEach((bg) => { map[bg.id] = bg; });
        setBackgroundsById(map);
      })
      .catch((err) => console.error('Failed to load backgrounds for set', backgroundSetId, err));
  }, [backgroundSetId]);

  const handlePackageSelect = useCallback((pkgId) => {
    setSelectedPackageId(pkgId);
    if (!pkgId) { setBackgroundSetId(null); return; }
    const pkg = packages.find((p) => p.id === pkgId);
    setBackgroundSetId(pkg?.background_set_id || null);
    // Prefer the layout that was last saved for this package, then fall back
    // to the package's own season so a brand-new pricelist (no saved layout
    // yet) still opens on its own season instead of leaking the initial
    // 'spring2026' default.
    const candidate = [pkg?.last_edited_season, pkg?.season].find(
      (s) => s && SEASON_VALUES.includes(s),
    );
    if (candidate) setSelectedSeason(candidate);
  }, [packages]);

  const loadLayout = useCallback(async () => {
    if (!selectedPackageId || !selectedSeason) return;
    setLoadingLayout(true);
    // Clear stale data immediately so the user sees the loading state, not old slots
    setSlots(DEFAULT_SLOTS.map(s => ({ ...s, look_template_id: null, background_id: null, background_image_url: null })));
    setLayoutId(null);
    setReferencePoses([]);
    try {
      // Fetch layout without reference_poses (fast — ~2 KB instead of 50+ MB)
      const data = await fetchLayout(selectedPackageId, selectedSeason);
      if (data) {
        setLayoutId(data.id);
        setBuyAllS3Path(data.buy_all_s3_path || null);
        // Merge saved slots with default slot structure
        const mergedSlots = DEFAULT_SLOTS.map(ds => {
          const saved = data.slots.find(s => s.slot_id === ds.slot_id);
          return {
            ...ds,
            look_template_id: saved?.look_template_id || null,
            background_id: saved?.background_id || null,
            background_image_url: saved?.background_image_url || null
          };
        });
        setSlots(mergedSlots);
      } else {
        setLayoutId(null);
        setBuyAllS3Path(null);
        setSlots(DEFAULT_SLOTS.map(s => ({ ...s, look_template_id: null, background_id: null, background_image_url: null })));
      }
      setDirty(false);
    } catch (err) {
      console.error('Failed to load layout:', err);
    }
    setLoadingLayout(false);

    // Load reference poses in the background (can be 50+ MB)
    fetchLayoutPoses(selectedPackageId, selectedSeason).then(poses => {
      setReferencePoses(poses);
      onReferencePosesChange?.(poses);
    }).catch(err => console.error('Failed to load reference poses:', err));
  }, [selectedPackageId, selectedSeason]);

  useEffect(() => { loadLayout(); }, [loadLayout]);

  const handleSlotChange = useCallback((slotId, lookTemplateId) => {
    setSlots(prev => prev.map(s =>
      s.slot_id === slotId ? { ...s, look_template_id: lookTemplateId } : s
    ));
    setDirty(true);
  }, []);

  const handleBuyAllChange = useCallback((s3Key) => {
    setBuyAllS3Path(s3Key);
    setDirty(true);
  }, []);

  const handleBackgroundChange = useCallback((slotId, bgId, bgUrl) => {
    setSlots(prev => prev.map(s =>
      s.slot_id === slotId ? { ...s, background_id: bgId, background_image_url: bgUrl } : s
    ));
    setDirty(true);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaveFlash(false);
    try {
      const result = await saveLayout({
        package_id: selectedPackageId,
        season: selectedSeason,
        buy_all_s3_path: buyAllS3Path,
        reference_poses: referencePoses,
        slots: slots
      });
      setLayoutId(result.id);
      // Remember the season this package was last edited for, so re-selecting
      // the package in this session snaps back to it (matches server truth).
      setPackages((prev) => prev.map((p) => {
        if (p.id !== selectedPackageId) return p;
        return { ...p, last_edited_season: selectedSeason };
      }));
      setDirty(false);
      setSaveFlash(true);
      setTimeout(() => setSaveFlash(false), 3000);
    } catch (err) {
      alert('Save failed: ' + (err.response?.data?.errors?.join(', ') || err.message));
    }
    setSaving(false);
  };

  // Compute look usage counts across slots
  const lookUsageCounts = useMemo(() => {
    const counts = {};
    slots.forEach(s => {
      if (s.look_template_id) {
        counts[s.look_template_id] = (counts[s.look_template_id] || 0) + 1;
      }
    });
    return counts;
  }, [slots]);

  // Drag handlers for slot reordering
  const handleDragStart = useCallback((slotId) => {
    dragSourceRef.current = slotId;
  }, []);

  const handleDragOver = useCallback((slotId) => {
    if (dragSourceRef.current && dragSourceRef.current !== slotId) {
      setDragOverSlotId(slotId);
    }
  }, []);

  const handleDragEnd = useCallback(() => {
    setDragOverSlotId(null);
    dragSourceRef.current = null;
  }, []);

  const handleDrop = useCallback((targetSlotId) => {
    const sourceSlotId = dragSourceRef.current;
    if (!sourceSlotId || sourceSlotId === targetSlotId) {
      handleDragEnd();
      return;
    }
    setSlots(prev => {
      const next = [...prev];
      const srcIdx = next.findIndex(s => s.slot_id === sourceSlotId);
      const tgtIdx = next.findIndex(s => s.slot_id === targetSlotId);
      if (srcIdx < 0 || tgtIdx < 0) return prev;
      // Swap look_template_id and background between slots
      const srcLook = next[srcIdx].look_template_id;
      const srcBg = next[srcIdx].background_id;
      const srcBgUrl = next[srcIdx].background_image_url;
      next[srcIdx] = { ...next[srcIdx], look_template_id: next[tgtIdx].look_template_id, background_id: next[tgtIdx].background_id, background_image_url: next[tgtIdx].background_image_url };
      next[tgtIdx] = { ...next[tgtIdx], look_template_id: srcLook, background_id: srcBg, background_image_url: srcBgUrl };
      return next;
    });
    setDirty(true);
    handleDragEnd();
  }, [handleDragEnd]);

  // Group slots by row
  const rows = [0, 1, 2, 3].map(rowNum =>
    slots.filter(s => s.row_number === rowNum)
  );

  return (
    <div style={{ height: '100%', overflow: 'auto', padding: 24 }}>
      {/* Selectors */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 24, flexWrap: 'nowrap' }}>
        <PackageSearch
          packages={packages}
          selectedPackageId={selectedPackageId}
          onSelect={handlePackageSelect}
        />
        <div>
          <label style={{ display: 'block', fontSize: 11, color: '#666', marginBottom: 2, fontWeight: 500 }}>Season</label>
          <select
            value={selectedSeason}
            onChange={e => setSelectedSeason(e.target.value)}
            style={{
              padding: '5px 8px', border: '1px solid #ccc', borderRadius: 4,
              fontSize: 13, background: '#fff'
            }}
          >
            {SEASONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !dirty || !selectedPackageId}
          style={{
            padding: '6px 14px',
            background: dirty && selectedPackageId ? '#1976d2' : '#bbb',
            color: '#fff', border: 'none', borderRadius: 4,
            cursor: dirty && selectedPackageId ? 'pointer' : 'default',
            fontSize: 13, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap'
          }}
        >
          {saving ? (
            <><InlineSpinner size={14} color="#fff" /> Saving...</>
          ) : (
            <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg> Save</>
          )}
        </button>
        {saveFlash && (
          <span style={{ fontSize: 11, color: '#2e7d32', fontWeight: 500, whiteSpace: 'nowrap' }}>
            ✓ Saved
          </span>
        )}
        <button
          onClick={() => setShowPreview(true)}
          style={{
            padding: '6px 14px', background: '#fff', border: '1px solid #ccc',
            borderRadius: 4, cursor: 'pointer', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap'
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg> Preview
        </button>
        <button
          onClick={() => setShowBatchTest(true)}
          disabled={!selectedPackageId}
          title="Apply this layout to a random sample of real students so you can spot per-look issues at a glance."
          style={{
            padding: '6px 14px', background: '#fff', border: '1px solid #ccc',
            borderRadius: 4, cursor: selectedPackageId ? 'pointer' : 'default',
            opacity: selectedPackageId ? 1 : 0.5,
            fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
          Batch Test
        </button>
        <CsvExport looks={looks} season={selectedSeason} />
        <PopularityReport season={selectedSeason} packageId={selectedPackageId} looks={looks} />
      </div>

      {loadingPackages ? (
        <FullPageSpinner label="Loading packages..." />
      ) : !selectedPackageId ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
          Select a package and season to configure the favorites layout
        </div>
      ) : loadingLayout || loading ? (
        <FullPageSpinner label={loadingLayout ? "Loading layout..." : "Loading looks..."} />
      ) : (
        <>
          {/* Background set + Reference poses — side by side */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap', alignItems: 'stretch' }}>
            {/* Pricelist-level background set */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '8px 12px', background: '#f5f5f5', borderRadius: 4, border: '1px solid #e0e0e0',
              alignSelf: 'flex-start'
            }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#555', whiteSpace: 'nowrap' }}>Background Set:</label>
              <select
                value={backgroundSetId || ''}
                onChange={e => { setBackgroundSetId(e.target.value ? parseInt(e.target.value, 10) : null); setDirty(true); }}
                style={{
                  padding: '5px 8px', border: '1px solid #ccc', borderRadius: 4,
                  fontSize: 13, background: '#fff', minWidth: 200
                }}
              >
                <option value="">None</option>
                {backgroundSets.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>

            {/* Reference Poses */}
            <div style={{ flex: 1, minWidth: 280 }}>
              <ReferencePoses
                poses={referencePoses}
                onChange={poses => { setReferencePoses(poses); setDirty(true); onReferencePosesChange?.(poses); }}
              />
            </div>
          </div>

          {/* Promo templates assigned to this pricelist */}
          <PromotionalImages packageId={selectedPackageId} />

          <div style={{ fontSize: 12, color: '#999', marginBottom: 12 }}>
            Assign a look template to each slot. The first slot is the Buy All banner (per pricelist). The remaining 11 slots are look images.
            {layoutId && <span style={{ marginLeft: 8, color: '#4caf50' }}>Layout saved (ID: {layoutId})</span>}
          </div>

          {/* Slot grid */}
          {rows.map((rowSlots, rowIdx) => (
            <div key={rowIdx} style={{
              display: 'flex', gap: 12, marginBottom: 12, alignItems: 'stretch'
            }}>
              <div style={{ width: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc', fontSize: 12 }}>
                Row {rowIdx + 1}
              </div>
              {rowSlots.map(slot => (
                slot.slot_type === 'buy_all' ? (
                  <BuyAllSlotCard
                    key={slot.slot_id}
                    buyAllS3Path={buyAllS3Path}
                    onBuyAllChange={handleBuyAllChange}
                  />
                ) : (
                  <SlotCard
                    key={slot.slot_id}
                    slot={slot}
                    looks={looks}
                    onSlotChange={handleSlotChange}
                    onBackgroundChange={handleBackgroundChange}
                    backgroundSetId={backgroundSetId}
                    backgroundsById={backgroundsById}
                    lookUsageCounts={lookUsageCounts}
                    isDragOver={dragOverSlotId === slot.slot_id}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onDragEnd={handleDragEnd}
                  />
                )
              ))}
            </div>
          ))}
        </>
      )}

      {showPreview && (
        <FavoritesPreview
          slots={slots.map((s) => (
            s.background_id && !s.background_image_url && backgroundsById[s.background_id]
              ? { ...s, background_image_url: backgroundsById[s.background_id].image_url }
              : s
          ))}
          looks={looks}
          buyAllS3Path={buyAllS3Path}
          referencePoses={referencePoses}
          onClose={() => setShowPreview(false)}
        />
      )}

      {showBatchTest && (
        <BatchTest
          slots={slots}
          looks={looks}
          backgroundsById={backgroundsById}
          layoutSeason={selectedSeason}
          onClose={() => setShowBatchTest(false)}
        />
      )}
    </div>
  );
};

export default FavoritesLayoutEditor;
